
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PlusCircle, Search, FilterX } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

// Sample members data
const allMembers = [
  {
    id: "1",
    name: "Alex Johnson",
    email: "alex@example.com",
    program: "Premium Rewards",
    programId: "1",
    points: 2500,
    status: "Active",
  },
  {
    id: "2",
    name: "Sam Smith",
    email: "sam@example.com",
    program: "Fitness Points",
    programId: "2",
    points: 1800,
    status: "Active",
  },
  {
    id: "3",
    name: "Taylor Williams",
    email: "taylor@example.com",
    program: "Reader Rewards",
    programId: "3",
    points: 950,
    status: "Active",
  },
  {
    id: "4",
    name: "Jordan Lee",
    email: "jordan@example.com",
    program: "Premium Rewards",
    programId: "1",
    points: 3200,
    status: "Inactive",
  },
  {
    id: "5",
    name: "Casey Morgan",
    email: "casey@example.com",
    program: "Tech Points",
    programId: "5",
    points: 750,
    status: "Active",
  },
  {
    id: "6",
    name: "Riley Parker",
    email: "riley@example.com",
    program: "Grocery Rewards",
    programId: "4",
    points: 1200,
    status: "Active",
  },
  {
    id: "7",
    name: "Jamie Garcia",
    email: "jamie@example.com",
    program: "Fitness Points",
    programId: "2",
    points: 2100,
    status: "Active",
  },
];

// Sample programs for filtering
const programOptions = [
  { label: "All Programs", value: "all" },
  { label: "Premium Rewards", value: "1" },
  { label: "Fitness Points", value: "2" },
  { label: "Reader Rewards", value: "3" },
  { label: "Grocery Rewards", value: "4" },
  { label: "Tech Points", value: "5" },
];

export default function PerkPointMembers() {
  // Get default programId from localStorage if available
  const savedProgramId = localStorage.getItem("selectedProgramId");
  const defaultProgramFilter = savedProgramId || "all";

  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [programFilter, setProgramFilter] = useState(defaultProgramFilter);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Save program filter to localStorage when it changes
  useEffect(() => {
    if (programFilter !== "all") {
      localStorage.setItem("selectedProgramId", programFilter);
    }
  }, [programFilter]);

  // Apply filters
  const filteredMembers = allMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          member.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesProgram = programFilter === "all" || member.programId === programFilter;
    
    const matchesStatus = statusFilter === "all" || 
                          (statusFilter === "active" && member.status === "Active") ||
                          (statusFilter === "inactive" && member.status === "Inactive");
    
    return matchesSearch && matchesProgram && matchesStatus;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredMembers.length / rowsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Reset filters
  const resetFilters = () => {
    setSearchQuery("");
    setProgramFilter("all");
    setStatusFilter("all");
    localStorage.removeItem("selectedProgramId");
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    
    // First page
    items.push(
      <PaginationItem key="first">
        <PaginationLink 
          onClick={() => handlePageChange(1)}
          isActive={currentPage === 1}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Ellipsis if needed
    if (currentPage > 3) {
      items.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Pages around current
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i >= 2 && i < totalPages) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink 
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    }
    
    // Ellipsis if needed
    if (currentPage < totalPages - 2) {
      items.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Last page if there are more than 1 page
    if (totalPages > 1) {
      items.push(
        <PaginationItem key="last">
          <PaginationLink 
            onClick={() => handlePageChange(totalPages)}
            isActive={currentPage === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Members</h1>
          <p className="text-muted-foreground">Manage your program members.</p>
        </div>
        <Button className="bg-green-600 hover:bg-green-700" asChild>
          <Link to="/perkpoint/create-member">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Member
          </Link>
        </Button>
      </div>

      {/* Filters section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              placeholder="Search members by name or email..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="w-full sm:w-auto">
          <label htmlFor="program" className="block text-sm font-medium text-gray-700 mb-1">Program</label>
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger id="program" className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full sm:w-auto">
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="status" className="w-full sm:w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          variant="outline" 
          size="icon"
          onClick={resetFilters}
          className="h-10 w-10 mt-6 sm:mt-0"
        >
          <FilterX className="h-4 w-4" />
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Members</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Points</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedMembers.length > 0 ? (
                paginatedMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.program}</TableCell>
                    <TableCell>{member.points}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={member.status === "Active" 
                          ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20" 
                          : "bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20"}
                      >
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/perkpoint/member/${member.id}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    No members found matching your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Rows per page:
              </p>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={rowsPerPage} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {totalPages > 0 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)} 
                      aria-disabled={currentPage === 1}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  
                  {getPaginationItems()}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                      aria-disabled={currentPage === totalPages}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
            
            <div className="text-sm text-muted-foreground">
              {filteredMembers.length > 0 
                ? `Showing ${(currentPage - 1) * rowsPerPage + 1} to ${Math.min(currentPage * rowsPerPage, filteredMembers.length)} of ${filteredMembers.length}` 
                : "No results"}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
